// useChatWebSocket Hook - WebSocket-based chat with SSE fallback
import { useState, useCallback, useRef, useEffect } from 'react';

const WS_BASE_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:5000';
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

export interface ChatMessage {
  id?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  isStreaming?: boolean;
  metadata?: {
    source?: 'openclaw' | 'openrouter';
    usedFallback?: boolean;
    agent?: string;
    agentName?: string;
  };
}

export interface ChatContext {
  opportunityId?: string;
  opportunity?: {
    title?: string;
    description?: string;
    agency?: string;
    setAside?: string;
    naicsCode?: string;
    deadline?: string;
    solicitationNumber?: string;
  };
  topic?: string; // Topic for agent routing
  agent?: string; // Explicit agent selection
  token?: string; // For WebSocket auth
}

export interface UseChatWebSocketReturn {
  messages: ChatMessage[];
  isLoading: boolean;
  error: string | null;
  conversationId: string | null;
  creditsUsed: number;
  creditsTotal: number;
  isConnected: boolean;
  transport: 'websocket' | 'sse' | null;
  sendMessage: (message: string, context?: ChatContext) => Promise<void>;
  loadConversation: (id: string) => Promise<void>;
  clearChat: () => void;
  stopStreaming: () => void;
}

export function useChatWebSocket(): UseChatWebSocketReturn {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [creditsTotal, setCreditsTotal] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [transport, setTransport] = useState<'websocket' | 'sse' | null>(null);
  
  const wsRef = useRef<WebSocket | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const streamingStateRef = useRef({
    fullResponse: '',
    usedFallback: false,
    source: 'openclaw' as 'openclaw' | 'openrouter',
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Connect WebSocket
  const connectWebSocket = useCallback((token: string): Promise<boolean> => {
    return new Promise((resolve) => {
      try {
        const wsUrl = `${WS_BASE_URL}/ws/chat`;
        const ws = new WebSocket(wsUrl);
        
        const timeout = setTimeout(() => {
          ws.close();
          resolve(false);
        }, 5000); // 5 second timeout

        ws.onopen = () => {
          clearTimeout(timeout);
          // Send auth message
          ws.send(JSON.stringify({
            type: 'start',
            token,
            conversationId: conversationId || undefined,
          }));
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            switch (data.type) {
              case 'connected':
                // Connection established, waiting for auth
                break;
                
              case 'authenticated':
                setIsConnected(true);
                setTransport('websocket');
                resolve(true);
                break;
                
              case 'error':
                if (data.error?.message?.includes('auth') || data.error?.message?.includes('token')) {
                  clearTimeout(timeout);
                  ws.close();
                  resolve(false);
                }
                break;
            }
          } catch (e) {
            // Ignore parse errors
          }
        };

        ws.onerror = () => {
          clearTimeout(timeout);
          resolve(false);
        };

        ws.onclose = () => {
          clearTimeout(timeout);
          setIsConnected(false);
        };

        wsRef.current = ws;
      } catch (e) {
        resolve(false);
      }
    });
  }, [conversationId]);

  // Send message via WebSocket
  const sendViaWebSocket = useCallback(async (
    message: string,
    context?: ChatContext,
    history?: ChatMessage[]
  ): Promise<boolean> => {
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      return false;
    }

    return new Promise((resolve) => {
      const ws = wsRef.current!;
      
      // Reset streaming state
      streamingStateRef.current = {
        fullResponse: '',
        usedFallback: false,
        source: 'openclaw',
      };

      const handleMessage = (event: MessageEvent) => {
        try {
          const data = JSON.parse(event.data);
          
          switch (data.type) {
            case 'stream_start':
              setIsLoading(true);
              if (data.data?.conversationId) {
                setConversationId(data.data.conversationId);
              }
              break;
              
            case 'stream_chunk':
              if (data.data) {
                streamingStateRef.current.fullResponse += data.data;
                setMessages(prev => {
                  const newMessages = [...prev];
                  const lastMessage = newMessages[newMessages.length - 1];
                  if (lastMessage && lastMessage.role === 'assistant') {
                    lastMessage.content = streamingStateRef.current.fullResponse;
                  }
                  return newMessages;
                });
              }
              break;
              
            case 'fallback':
              streamingStateRef.current.usedFallback = true;
              streamingStateRef.current.source = 'openrouter';
              break;
              
            case 'stream_complete':
              if (data.data) {
                streamingStateRef.current.fullResponse = data.data;
              }
              if (data.metadata) {
                setCreditsUsed(data.metadata.creditsUsed || 0);
                setCreditsTotal(data.metadata.creditsTotal || 0);
              }
              
              // Mark as complete
              const finalState = { ...streamingStateRef.current };
              setMessages(prev => {
                const newMessages = [...prev];
                const lastMessage = newMessages[newMessages.length - 1];
                if (lastMessage && lastMessage.role === 'assistant') {
                  lastMessage.content = finalState.fullResponse;
                  lastMessage.isStreaming = false;
                  lastMessage.metadata = {
                    source: finalState.source,
                    usedFallback: finalState.usedFallback,
                  };
                }
                return newMessages;
              });
              
              setIsLoading(false);
              ws.removeEventListener('message', handleMessage);
              resolve(true);
              break;
              
            case 'error':
              setError(data.error?.message || 'WebSocket error');
              setIsLoading(false);
              ws.removeEventListener('message', handleMessage);
              resolve(false);
              break;
          }
        } catch (e) {
          // Ignore parse errors
        }
      };

      ws.addEventListener('message', handleMessage);

      // Send the message
      ws.send(JSON.stringify({
        type: 'message',
        message,
        conversationId,
        context: context ? {
          ...context,
          token: undefined, // Remove token from context
        } : undefined,
        history: history?.slice(-10).map(m => ({
          role: m.role,
          content: m.content,
        })),
      }));

      // Timeout after 5 minutes
      setTimeout(() => {
        ws.removeEventListener('message', handleMessage);
        resolve(false);
      }, 300000);
    });
  }, [conversationId]);

  // Send message via SSE (fallback)
  const sendViaSSE = useCallback(async (
    message: string,
    context?: ChatContext,
    history?: ChatMessage[]
  ): Promise<boolean> => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('Not authenticated');
    }

    try {
      const response = await fetch(`${API_BASE_URL}/ai/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Accept': 'text/event-stream',
        },
        body: JSON.stringify({
          message,
          conversationId,
          context: context ? {
            ...context,
            token: undefined,
          } : undefined,
          history: history?.slice(-10).map(m => ({
            role: m.role,
            content: m.content,
          })),
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      setTransport('sse');
      const newConversationId = response.headers.get('X-Conversation-Id');
      if (newConversationId) {
        setConversationId(newConversationId);
      }

      // Read the stream
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';
      
      streamingStateRef.current = {
        fullResponse: '',
        usedFallback: false,
        source: 'openclaw',
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const event = JSON.parse(line.slice(6));
              
              switch (event.type) {
                case 'chunk':
                  if (event.data) {
                    streamingStateRef.current.fullResponse += event.data;
                    const currentResponse = streamingStateRef.current.fullResponse;
                    setMessages(prev => {
                      const newMessages = [...prev];
                      const lastMessage = newMessages[newMessages.length - 1];
                      if (lastMessage && lastMessage.role === 'assistant') {
                        lastMessage.content = currentResponse;
                      }
                      return newMessages;
                    });
                  }
                  break;
                  
                case 'fallback':
                  streamingStateRef.current.usedFallback = true;
                  streamingStateRef.current.source = 'openrouter';
                  break;
                  
                case 'complete':
                  if (event.data) {
                    streamingStateRef.current.fullResponse = event.data;
                  }
                  if (event.metadata) {
                    setCreditsUsed(event.metadata.creditsUsed || 0);
                    setCreditsTotal(event.metadata.creditsTotal || 0);
                  }
                  break;
              }
            } catch (e) {
              // Ignore parse errors
            }
          }
        }
      }

      // Mark as complete
      const finalState = { ...streamingStateRef.current };
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && lastMessage.role === 'assistant') {
          lastMessage.content = finalState.fullResponse;
          lastMessage.isStreaming = false;
          lastMessage.metadata = {
            source: finalState.source,
            usedFallback: finalState.usedFallback,
          };
        }
        return newMessages;
      });

      return true;
    } catch (err) {
      console.error('SSE error:', err);
      return false;
    }
  }, [conversationId]);

  const sendMessage = useCallback(async (message: string, context?: ChatContext) => {
    if (!message.trim()) return;

    setError(null);

    // Add user message immediately
    const userMessage: ChatMessage = {
      role: 'user',
      content: message,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    // Add placeholder for assistant response
    const assistantPlaceholder: ChatMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };
    setMessages(prev => [...prev, assistantPlaceholder]);

    try {
      // Try WebSocket first
      const token = localStorage.getItem('accessToken');
      if (!token) {
        throw new Error('Not authenticated');
      }

      // Add token to context for WebSocket auth
      const contextWithToken = { ...context, token };

      // Try to connect WebSocket if not connected
      let wsSuccess = false;
      if (!isConnected || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        const connected = await connectWebSocket(token);
        if (connected) {
          wsSuccess = await sendViaWebSocket(message, contextWithToken, messages);
        }
      } else {
        wsSuccess = await sendViaWebSocket(message, contextWithToken, messages);
      }

      // If WebSocket failed, fall back to SSE
      if (!wsSuccess) {
        console.log('WebSocket failed, falling back to SSE');
        const sseSuccess = await sendViaSSE(message, context, messages);
        
        if (!sseSuccess) {
          throw new Error('Both WebSocket and SSE failed');
        }
      }
    } catch (err: any) {
      console.error('Chat error:', err);
      setError(err.message || 'Failed to send message');
      
      // Update the assistant placeholder with error
      setMessages(prev => {
        const newMessages = [...prev];
        const lastMessage = newMessages[newMessages.length - 1];
        if (lastMessage && lastMessage.role === 'assistant') {
          lastMessage.content = 'I apologize, but I encountered an error processing your question. Please try again.';
          lastMessage.isStreaming = false;
        }
        return newMessages;
      });
    } finally {
      setIsLoading(false);
    }
  }, [messages, isConnected, connectWebSocket, sendViaWebSocket, sendViaSSE]);

  const clearChat = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setError(null);
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setIsConnected(false);
    setTransport(null);
  }, []);

  const stopStreaming = useCallback(() => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: 'stop' }));
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsLoading(false);
    
    setMessages(prev => {
      const newMessages = [...prev];
      const lastMessage = newMessages[newMessages.length - 1];
      if (lastMessage && lastMessage.isStreaming) {
        lastMessage.isStreaming = false;
      }
      return newMessages;
    });
  }, []);

  const loadConversation = useCallback(async (id: string) => {
    try {
      console.log('[ChatHistory] Loading conversation from API:', id);
      const response = await fetch(`${API_BASE_URL}/ai/chat/conversations/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Failed to load conversation: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('[ChatHistory] API response:', data);
      const conversation = data.data;
      
      if (conversation) {
        console.log('[ChatHistory] Setting conversation:', conversation.id, 'messages:', conversation.messages?.length);
        setConversationId(conversation.id);
        setMessages(conversation.messages.map((m: any) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          timestamp: new Date(m.createdAt || m.timestamp),
          metadata: m.metadata,
        })));
      } else {
        console.log('[ChatHistory] No conversation data in response');
      }
    } catch (err) {
      console.error('[ChatHistory] Failed to load conversation:', err);
    }
  }, []);

  return {
    messages,
    isLoading,
    error,
    conversationId,
    creditsUsed,
    creditsTotal,
    isConnected,
    transport,
    sendMessage,
    loadConversation,
    clearChat,
    stopStreaming,
  };
}

export default useChatWebSocket;
